import { Component, Input, OnDestroy, OnInit, signal, Type } from '@angular/core';
import { of, Subject } from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { createVariablesRecord, DoodBlockModel } from '@core/models/block.model';
import { CamelCaseUtils } from '@app/utilities/camel-case.util';
import { DoodRenderingBlock } from '@shared/interfaces/block.interface';
import { ContentStoreSelector } from '@common/selectors/content.selector';
import { ALLOWED_BLOCKS } from '@config/blocks.config';
import { EvaluateBlockVisibilityService } from '@core/services/content-builder/evaluate-block-visibility.service';

@Component({
  selector: 'app-block-factory',
  templateUrl: './block-factory.component.html',
  styleUrls: ['./block-factory.component.scss'],
})
export class BlockFactoryComponent implements OnInit, OnDestroy {
  private _destroyerRef = new Subject<boolean>();

  // data coming from the content builder
  data: Record<string, unknown> = {};
  componentClass?: Type<unknown> | string;

  // injectData, is data coming from the component that open the modal
  @Input() injectData: Record<string, unknown> = {};
  @Input() blockId?: string | Partial<DoodRenderingBlock>;

  protected readonly isVisible = signal<boolean>(false);

  constructor(
    private blockSelector: ContentStoreSelector,
    private readonly evaluateBlockVisibilityService: EvaluateBlockVisibilityService,
  ) {}

  // TODO: Need to avoid modifying blockId input value
  ngOnInit(): void {
    if (!this.blockId) return;

    if (typeof this.blockId !== 'string') {
      if (this.blockId.componentClass) {
        this.data = this.blockId.data ?? {};
        this.componentClass = this.blockId.componentClass;
      }

      if (this.blockId.block) {
        this.blockId = this.blockId.block;
      }
    }

    this.blockSelector
      .selectBlock(this.blockId.toString())
      .pipe(
        tap((_block?: DoodBlockModel) => {
          if (_block) {
            this._renderComponent(_block);
          }
        }),
        switchMap(_block => {
          if (!_block) {
            return of(false);
          }
          const conditionalVisibility = _block.data?.common?.conditionalVisibility;
          if (!conditionalVisibility || !conditionalVisibility.expression) {
            return of(true);
          }
          return this.evaluateBlockVisibilityService
            .evaluate$(conditionalVisibility.expression, {
              additionalContext: createVariablesRecord(_block.data?.common?.variables ?? []),
            })
            .pipe(map(expressionResult => !!expressionResult));
        }),
        takeUntil(this._destroyerRef),
      )
      .subscribe(isVisible => {
        this.isVisible.set(isVisible);
      });
  }

  ngOnDestroy(): void {
    this._destroyerRef.next(true);
    this._destroyerRef.complete();
  }

  private _renderComponent(_descriptor: DoodBlockModel): void {
    if (!_descriptor) {
      console.error('[BlockFactory] Error: Block ' + this.blockId + ' is not found');
      return;
    }

    let _type = _descriptor.type;

    if (!_type.endsWith('Component')) {
      _type = _type + 'Component';
    }

    if (!ALLOWED_BLOCKS.hasOwnProperty(_type)) {
      console.debug('[BlockFactory] blockDescriptor: ', _descriptor);
      console.error('[BlockFactory] Error: Component ' + _type + ' is not registered');
      return;
    }

    this.componentClass = Object(ALLOWED_BLOCKS)[_type];
    this.data = {
      ...CamelCaseUtils.convertAttributeNamesToCamelCase(_descriptor.data),
      blockId: _descriptor.id,
    };

    const isModalOpened = !!this.injectData;
    if (isModalOpened) {
      // surcharge les data pouvant venir du content builder par celle pouvant venir du composant qui ouvre la modal
      Object.entries(this.injectData).forEach(([key, value]) => {
        if (typeof this.data[key] === 'object' && typeof value === 'object') {
          this.injectData[key] = { ...(this.data[key] as object), ...value };
        }
      });
    }
    // surcharge les data pouvant venir du content builder par celle pouvant venir du composant qui ouvre la modal
    this.data = { ...this.data, ...this.injectData };
  }
}
